import { utcYear } from 'd3-time';
import {
  X_AXIS_OFFSET_LEFT,
  X_AXIS_OFFSET_RIGHT,
  X_AXIS_PADDING_LEFT,
  X_AXIS_PADDING_RIGHT,
  X_AXIS_TICK_LABEL_WIDTH,
} from '../lib';
import { useMemo, useRef } from 'react';
import { scaleUtc } from 'd3-scale';

type UseXAxisConfigProps = {
  chartWidth: number;
  currentYear: number;
  minYear: number | undefined;
  maxYear: number | undefined;
};

export const useXAxisConfig = ({
  chartWidth,
  currentYear,
  minYear,
  maxYear,
}: UseXAxisConfigProps) => {
  const { current: xScale } = useRef(scaleUtc<number>());

  // X Scale config
  xScale.range([X_AXIS_OFFSET_LEFT, chartWidth - X_AXIS_OFFSET_RIGHT]);
  xScale.domain([
    minYear ? new Date(minYear.toString()) : new Date(),
    maxYear ? new Date(maxYear.toString()) : new Date(),
  ]);

  /**
   * Dynamically calculates years to render onto X Axis.
   * It always preserves important dates such as Min year, Current year and Max year.
   * Then it spaces the years according to how many ticks can fit
   * given the chart size making sure no tick is too close to each other to overlap.
   */
  const tickValues = useMemo(() => {
    const ticksMaximumCount = Math.floor(
      (chartWidth -
        X_AXIS_OFFSET_LEFT -
        X_AXIS_OFFSET_RIGHT -
        X_AXIS_PADDING_LEFT -
        X_AXIS_PADDING_RIGHT) /
        X_AXIS_TICK_LABEL_WIDTH
    );

    const interval = utcYear.every(1);
    const ticks =
      interval !== null
        ? xScale.ticks(interval)
        : xScale.ticks(ticksMaximumCount);
    const numOfTicksTotal = ticks.length;
    const tickSpacing = numOfTicksTotal / ticksMaximumCount;
    const reservedIndexes = [
      0,
      ticks.findIndex((date) => date.getUTCFullYear() === currentYear),
      ticks.length - 1,
    ];

    return xScale.ticks(numOfTicksTotal).filter((_value, index) => {
      const isReserved = reservedIndexes.includes(index);
      const isInBetweenTick =
        index % Math.ceil(tickSpacing) === 0 &&
        reservedIndexes.every(
          (reservedIndex) => Math.abs(index - reservedIndex) >= tickSpacing
        );
      return isReserved || isInBetweenTick;
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartWidth, minYear, maxYear, currentYear, xScale]);

  return {
    xScale,
    tickValues,
    tickFormat: xScale.tickFormat(undefined, '%Y'),
  };
};
